
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {CurrencyModifyResponse} from './CurrencyModifyResponse';

/**
 * CurrencyModifyRequest struct
 */
class CurrencyModifyRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} currencyId
   * @param {string=} currencyName
   * @param {number=} newSortOrder
   * @param {UUID=} newCurrencyGroupId
   * @param {string=} newCurrencyGroupName
   * @param {string=} newName
   * @param {string=} newComment
   * @param {string=} newDescription
   * @param {number=} newPrecision
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), currencyId = null, currencyName = null, newSortOrder = null, newCurrencyGroupId = null, newCurrencyGroupName = null, newName = null, newComment = null, newDescription = null, newPrecision = null) {
    super()
    super.copy(parent)
    this.CurrencyId = currencyId
    this.CurrencyName = currencyName
    this.NewSortOrder = newSortOrder
    this.NewCurrencyGroupId = newCurrencyGroupId
    this.NewCurrencyGroupName = newCurrencyGroupName
    this.NewName = newName
    this.NewComment = newComment
    this.NewDescription = newDescription
    this.NewPrecision = newPrecision
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CurrencyModifyRequest}
   * @param {!CurrencyModifyRequest} other Other struct
   * @returns {!CurrencyModifyRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.CurrencyId != null) {
      this.CurrencyId = new UUID(other.CurrencyId)
    } else {
      this.CurrencyId = null
    }
    if (other.CurrencyName != null) {
      this.CurrencyName = other.CurrencyName
    } else {
      this.CurrencyName = null
    }
    if (other.NewSortOrder != null) {
      this.NewSortOrder = other.NewSortOrder
    } else {
      this.NewSortOrder = null
    }
    if (other.NewCurrencyGroupId != null) {
      this.NewCurrencyGroupId = new UUID(other.NewCurrencyGroupId)
    } else {
      this.NewCurrencyGroupId = null
    }
    if (other.NewCurrencyGroupName != null) {
      this.NewCurrencyGroupName = other.NewCurrencyGroupName
    } else {
      this.NewCurrencyGroupName = null
    }
    if (other.NewName != null) {
      this.NewName = other.NewName
    } else {
      this.NewName = null
    }
    if (other.NewComment != null) {
      this.NewComment = other.NewComment
    } else {
      this.NewComment = null
    }
    if (other.NewDescription != null) {
      this.NewDescription = other.NewDescription
    } else {
      this.NewDescription = null
    }
    if (other.NewPrecision != null) {
      this.NewPrecision = other.NewPrecision
    } else {
      this.NewPrecision = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CurrencyModifyRequest}
   * @returns {!CurrencyModifyRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CurrencyModifyRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CurrencyModifyRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CurrencyModifyRequest}
   * @param {!CurrencyModifyRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CurrencyModifyRequest)) {
      throw new TypeError('Instance of CurrencyModifyRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CurrencyModifyRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      CurrencyId: ((this.CurrencyId != null) ? this.CurrencyId.toString() : null),
      CurrencyName: ((this.CurrencyName != null) ? this.CurrencyName : null),
      NewSortOrder: ((this.NewSortOrder != null) ? this.NewSortOrder : null),
      NewCurrencyGroupId: ((this.NewCurrencyGroupId != null) ? this.NewCurrencyGroupId.toString() : null),
      NewCurrencyGroupName: ((this.NewCurrencyGroupName != null) ? this.NewCurrencyGroupName : null),
      NewName: ((this.NewName != null) ? this.NewName : null),
      NewComment: ((this.NewComment != null) ? this.NewComment : null),
      NewDescription: ((this.NewDescription != null) ? this.NewDescription : null),
      NewPrecision: ((this.NewPrecision != null) ? this.NewPrecision : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CurrencyModifyRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CurrencyModifyRequest} other Object value
   * @returns {!CurrencyModifyRequest} Created struct
   */
  static fromObject (other) {
    return new CurrencyModifyRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CurrencyModifyRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CurrencyModifyRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CurrencyModifyRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6091
  }
}

export { CurrencyModifyRequest };

/**
 * Fast Binary Encoding CurrencyModifyRequest field model
 */
class FieldModelCurrencyModifyRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.CurrencyId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.CurrencyName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.CurrencyId.fbeOffset + this.CurrencyId.fbeSize), buffer, this.CurrencyId.fbeOffset + this.CurrencyId.fbeSize)
    this.NewSortOrder = new fbe.FieldModelOptional(new fbe.FieldModelUInt32(buffer, this.CurrencyName.fbeOffset + this.CurrencyName.fbeSize), buffer, this.CurrencyName.fbeOffset + this.CurrencyName.fbeSize)
    this.NewCurrencyGroupId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this.NewSortOrder.fbeOffset + this.NewSortOrder.fbeSize), buffer, this.NewSortOrder.fbeOffset + this.NewSortOrder.fbeSize)
    this.NewCurrencyGroupName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewCurrencyGroupId.fbeOffset + this.NewCurrencyGroupId.fbeSize), buffer, this.NewCurrencyGroupId.fbeOffset + this.NewCurrencyGroupId.fbeSize)
    this.NewName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewCurrencyGroupName.fbeOffset + this.NewCurrencyGroupName.fbeSize), buffer, this.NewCurrencyGroupName.fbeOffset + this.NewCurrencyGroupName.fbeSize)
    this.NewComment = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewName.fbeOffset + this.NewName.fbeSize), buffer, this.NewName.fbeOffset + this.NewName.fbeSize)
    this.NewDescription = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize), buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize)
    this.NewPrecision = new fbe.FieldModelOptional(new fbe.FieldModelUInt8(buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize), buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.CurrencyId.fbeSize + this.CurrencyName.fbeSize + this.NewSortOrder.fbeSize + this.NewCurrencyGroupId.fbeSize + this.NewCurrencyGroupName.fbeSize + this.NewName.fbeSize + this.NewComment.fbeSize + this.NewDescription.fbeSize + this.NewPrecision.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.CurrencyId.fbeExtra + this.CurrencyName.fbeExtra + this.NewSortOrder.fbeExtra + this.NewCurrencyGroupId.fbeExtra + this.NewCurrencyGroupName.fbeExtra + this.NewName.fbeExtra + this.NewComment.fbeExtra + this.NewDescription.fbeExtra + this.NewPrecision.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCurrencyModifyRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6091
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CurrencyId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CurrencyId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyId.fbeSize

    if ((fbeCurrentSize + this.CurrencyName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CurrencyName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyName.fbeSize

    if ((fbeCurrentSize + this.NewSortOrder.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewSortOrder.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewSortOrder.fbeSize

    if ((fbeCurrentSize + this.NewCurrencyGroupId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewCurrencyGroupId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewCurrencyGroupId.fbeSize

    if ((fbeCurrentSize + this.NewCurrencyGroupName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewCurrencyGroupName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewCurrencyGroupName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewComment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewDescription.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewPrecision.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewPrecision.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewPrecision.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {!CurrencyModifyRequest} fbeValue Default value, defaults is new CurrencyModifyRequest()
   * @returns {!CurrencyModifyRequest} CurrencyModifyRequest value
   */
  get (fbeValue = new CurrencyModifyRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {!CurrencyModifyRequest} fbeValue CurrencyModifyRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CurrencyId.fbeSize) <= fbeStructSize) {
      fbeValue.CurrencyId = this.CurrencyId.get()
    } else {
      fbeValue.CurrencyId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyId.fbeSize

    if ((fbeCurrentSize + this.CurrencyName.fbeSize) <= fbeStructSize) {
      fbeValue.CurrencyName = this.CurrencyName.get()
    } else {
      fbeValue.CurrencyName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyName.fbeSize

    if ((fbeCurrentSize + this.NewSortOrder.fbeSize) <= fbeStructSize) {
      fbeValue.NewSortOrder = this.NewSortOrder.get()
    } else {
      fbeValue.NewSortOrder = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewSortOrder.fbeSize

    if ((fbeCurrentSize + this.NewCurrencyGroupId.fbeSize) <= fbeStructSize) {
      fbeValue.NewCurrencyGroupId = this.NewCurrencyGroupId.get()
    } else {
      fbeValue.NewCurrencyGroupId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewCurrencyGroupId.fbeSize

    if ((fbeCurrentSize + this.NewCurrencyGroupName.fbeSize) <= fbeStructSize) {
      fbeValue.NewCurrencyGroupName = this.NewCurrencyGroupName.get()
    } else {
      fbeValue.NewCurrencyGroupName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewCurrencyGroupName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) <= fbeStructSize) {
      fbeValue.NewName = this.NewName.get()
    } else {
      fbeValue.NewName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) <= fbeStructSize) {
      fbeValue.NewComment = this.NewComment.get()
    } else {
      fbeValue.NewComment = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) <= fbeStructSize) {
      fbeValue.NewDescription = this.NewDescription.get()
    } else {
      fbeValue.NewDescription = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewPrecision.fbeSize) <= fbeStructSize) {
      fbeValue.NewPrecision = this.NewPrecision.get()
    } else {
      fbeValue.NewPrecision = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewPrecision.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCurrencyModifyRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {!CurrencyModifyRequest} fbeValue CurrencyModifyRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCurrencyModifyRequest}
   * @param {CurrencyModifyRequest} fbeValue CurrencyModifyRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.CurrencyId.set(fbeValue.CurrencyId)
    this.CurrencyName.set(fbeValue.CurrencyName)
    this.NewSortOrder.set(fbeValue.NewSortOrder)
    this.NewCurrencyGroupId.set(fbeValue.NewCurrencyGroupId)
    this.NewCurrencyGroupName.set(fbeValue.NewCurrencyGroupName)
    this.NewName.set(fbeValue.NewName)
    this.NewComment.set(fbeValue.NewComment)
    this.NewDescription.set(fbeValue.NewDescription)
    this.NewPrecision.set(fbeValue.NewPrecision)
  }
}

export { FieldModelCurrencyModifyRequest };

/**
 * Fast Binary Encoding CurrencyModifyRequest model
 */
class CurrencyModifyRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCurrencyModifyRequest(this.buffer, 4)
  }

  /**
   * Get the CurrencyModifyRequest model
   * @this {!CurrencyModifyRequestModel}
   * @returns {!FieldModelCurrencyModifyRequest} model CurrencyModifyRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CurrencyModifyRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CurrencyModifyRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CurrencyModifyRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CurrencyModifyRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCurrencyModifyRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CurrencyModifyRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CurrencyModifyRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CurrencyModifyRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CurrencyModifyRequestModel}
   * @param {!CurrencyModifyRequest} value CurrencyModifyRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CurrencyModifyRequestModel}
   * @param {!CurrencyModifyRequest} value CurrencyModifyRequest value, defaults is new CurrencyModifyRequest()
   * @return {!object} Deserialized CurrencyModifyRequest value and its size
   */
  deserialize (value = new CurrencyModifyRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CurrencyModifyRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CurrencyModifyRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CurrencyModifyRequestModel}
   * @param {!number} prev Previous CurrencyModifyRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CurrencyModifyRequestModel };
CurrencyModifyRequest.__has_response = true;
CurrencyModifyRequest.__response_class = CurrencyModifyResponse;
